import React, { useEffect, useState } from 'react';
import Styles from '../assets/css/productView.module.scss';
import { useNavigate, useParams } from "react-router-dom";
import { getProductDetailsById, getCategories } from '../services/Api';
import Loader from '../utils/Loader';
import { useBarcode } from 'react-barcodes';

const ProductView = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [productDetails, setProductDetails] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [activeSkuIndex, setActiveSkuIndex] = useState(0);
    const [categoryName, setCategoryName] = useState('N/A');

    const BarcodeGenerator = ({ value }) => {
        const { inputRef } = useBarcode({
            value: value || 'NO_BARCODE',
            options: {
                background: '#ffffff',
                width: 2,
                height: 100,
                displayValue: true,
                format: "CODE128"
            }
        });

        return (
            <div className={Styles.barcodePreview}>
                {value && <canvas ref={inputRef} />}
            </div>
        );
    };

    // Function to fetch category name using product name
    const fetchCategoryName = async (productName, categoryId) => {
        if (!productName) return 'N/A';

        try {
            const response = await getCategories(productName);
            if (response && response.status === 200) {
                // Find the category that matches the categoryId
                const categories = response.data?.data?.categorySuggestions || [];
                const matchedCategory = categories.find(cat => cat.categoryId === categoryId);

                return matchedCategory ? matchedCategory.categoryName : 'N/A';
            }
        } catch (error) {
            console.error("Error fetching category name:", error);
        }
        return 'N/A';
    };

    useEffect(() => {
        const fetchProductDetails = async () => {
            setIsLoading(true);
            try {
                const data = await getProductDetailsById(id);
                if (data && data.status === 200) {
                    setProductDetails(data.data);

                    // Fetch category name using product name and category ID
                    const productName = data.data.lazada_product?.data?.attributes?.name;
                    const categoryId = data.data.lazada_product?.data?.primary_category;

                    if (productName && categoryId) {
                        const name = await fetchCategoryName(productName, categoryId);
                        setCategoryName(name);
                    }
                } else {
                    console.error("Product not found or invalid response format:", data);
                    alert("Product not found. Please check the product ID.");
                    navigate("/company");
                }
            } catch (error) {
                console.error("Error fetching product details:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchProductDetails();
        } else {
            alert("Invalid product ID.");
            navigate("/company");
        }
    }, [id, navigate]);

    const handleChangeImage = (index) => {
        if (index === currentIndex) return;
        setIsTransitioning(true);
        setCurrentIndex(index);
        setTimeout(() => setIsTransitioning(false), 300);
    };

    const handlePrevImage = () => {
        if (!productDetails?.product_image?.length) return;
        const newIndex = (currentIndex - 1 + productDetails.product_image.length) % productDetails.product_image.length;
        handleChangeImage(newIndex);
    };

    const handleNextImage = () => {
        if (!productDetails?.product_image?.length) return;
        const newIndex = (currentIndex + 1) % productDetails.product_image.length;
        handleChangeImage(newIndex);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                handlePrevImage();
            } else if (e.key === 'ArrowRight') {
                handleNextImage();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, productDetails]);

    useEffect(() => {
        let interval;
        if (productDetails?.product_image?.length > 1) {
            interval = setInterval(() => {
                handleNextImage();
            }, 5000);
        }
        return () => clearInterval(interval);
    }, [currentIndex, productDetails]);

    const getCurrentImageUrl = () => {
        if (!productDetails?.product_image?.length) return '';
        return productDetails.product_image[currentIndex]?.image_url || '';
    };

    const getPaginationText = () => {
        if (!productDetails?.product_image?.length) return '';
        return `${currentIndex + 1} / ${productDetails.product_image.length}`;
    };

    const lazadaSkus = productDetails?.lazada_product?.data?.skus || [];
    const currentSku = lazadaSkus[activeSkuIndex] || {};

    // Check if product is perishable (has storage_type and expire_date)
    const isPerishableProduct = productDetails?.storage_type && productDetails?.expire_date;

    return (
        <div className={Styles.productView}>
            <div className={Styles.header}>
                <h1 className={Styles.title}>Product Details</h1>
                <button className={Styles.backBtn} onClick={() => navigate("/products")}>
                    Back
                </button>
            </div>

            {isLoading ? (
                <div className={Styles.loaderContainer}>
                    <Loader size="large" color="primary" />
                </div>
            ) : (
                <div className={Styles.container}>
                    {/* Image Gallery Section */}
                    <div className={Styles.imageSection}>
                        <div className={Styles.imageCard}>
                            <div className={Styles.mainImageWrapper}>
                                <div className={`${Styles.mainImage} ${isTransitioning ? Styles.transitioning : ''}`}>
                                    <img
                                        src={getCurrentImageUrl()}
                                        alt="Product"
                                        onError={(e) => {
                                            e.target.src = '/api/placeholder/400/400';
                                            e.target.alt = 'Image not available';
                                        }}
                                    />
                                </div>

                                {productDetails?.product_image?.length > 1 && (
                                    <>
                                        <button className={`${Styles.navBtn} ${Styles.prevBtn}`} onClick={handlePrevImage} aria-label="Previous">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="15 18 9 12 15 6" />
                                            </svg>
                                        </button>

                                        <button className={`${Styles.navBtn} ${Styles.nextBtn}`} onClick={handleNextImage} aria-label="Next">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="9 18 15 12 9 6" />
                                            </svg>
                                        </button>

                                        <div className={Styles.imageCounter}>{getPaginationText()}</div>
                                    </>
                                )}
                            </div>

                            {productDetails?.product_image?.length > 1 && (
                                <div className={Styles.thumbnailContainer}>
                                    {productDetails.product_image.map((imageObj, index) => (
                                        <div
                                            key={index}
                                            className={`${Styles.thumbnail} ${currentIndex === index ? Styles.activeThumbnail : ''}`}
                                            onClick={() => handleChangeImage(index)}
                                        >
                                            <img
                                                src={imageObj.image_url}
                                                alt={`Thumbnail ${index + 1}`}
                                                onError={(e) => {
                                                    e.target.src = '/api/placeholder/80/80';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Product Details Section */}
                    <div className={Styles.detailsSection}>
                        {/* Basic Information */}
                        <div className={Styles.card}>
                            <div className={Styles.cardHeader}>
                                <h2 className={Styles.productName}>{productDetails?.product_name || 'N/A'}</h2>
                                <span className={Styles.brandBadge}>{productDetails?.lazada_product.data?.attributes?.brand || 'No Brand'}</span>
                            </div>

                            <div className={Styles.infoGrid}>
                                <div className={Styles.infoItem}>
                                    <span className={Styles.infoIcon}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                    <div className={Styles.infoContent}>
                                        <label>SKU Number</label>
                                        <p>{productDetails?.sku_number || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Conditionally show Storage Type only for perishable products */}
                                {isPerishableProduct && (
                                    <div className={Styles.infoItem}>
                                        <span className={Styles.infoIcon}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <rect x="2" y="4" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                                <path d="M5 4V2.5C5 2.22386 5.22386 2 5.5 2H10.5C10.7761 2 11 2.22386 11 2.5V4" stroke="currentColor" strokeWidth="1.5" />
                                            </svg>
                                        </span>
                                        <div className={Styles.infoContent}>
                                            <label>Storage Type</label>
                                            <p>{productDetails?.storage_type || 'N/A'}</p>
                                        </div>
                                    </div>
                                )}

                                <div className={Styles.infoItem}>
                                    <span className={Styles.infoIcon}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="1" y="5" width="14" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                            <path d="M4 5V3.5C4 2.67157 4.67157 2 5.5 2H10.5C11.3284 2 12 2.67157 12 3.5V5" stroke="currentColor" strokeWidth="1.5" />
                                        </svg>
                                    </span>
                                    <div className={Styles.infoContent}>
                                        <label>Warehouse</label>
                                        <p>{productDetails?.warehouse?.name || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className={Styles.infoItem}>
                                    <span className={Styles.infoIcon}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="2" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                            <path d="M2 6h12" stroke="currentColor" strokeWidth="1.5" />
                                        </svg>
                                    </span>
                                    <div className={Styles.infoContent}>
                                        <label>Company</label>
                                        <p>{productDetails?.company?.company_name || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className={Styles.infoItem}>
                                    <span className={Styles.infoIcon}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                            <path d="M5 2v4M11 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                    <div className={Styles.infoContent}>
                                        <label>Batch Number</label>
                                        <p>{productDetails?.batch_number || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Conditionally show Expire Date only for perishable products */}
                                {isPerishableProduct && (
                                    <div className={Styles.infoItem}>
                                        <span className={Styles.infoIcon}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                                                <path d="M8 4v4l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            </svg>
                                        </span>
                                        <div className={Styles.infoContent}>
                                            <label>Expire Date</label>
                                            <p>{productDetails?.expire_date || 'N/A'}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {productDetails?.description && (
                                <div className={Styles.descriptionBox}>
                                    <label>Description</label>
                                    <div
                                        className={Styles.descriptionContent}
                                        dangerouslySetInnerHTML={{
                                            __html: productDetails.description
                                                .replace(/<figure class="table">/g, '<div class="table-container">')
                                                .replace(/<\/figure>/g, '</div>')
                                                .replace(/<table>/g, '<table class="description-table">')
                                        }}
                                    />
                                </div>
                            )}

                            {productDetails?.barcode_number && (
                                <div className={Styles.barcodeSection}>
                                    <label>Barcode</label>
                                    <BarcodeGenerator value={productDetails.barcode_number} />
                                </div>
                            )}
                        </div>

                        {/* Lazada Details */}
                        {productDetails?.lazada_product && (
                            <div className={Styles.card}>
                                <div className={Styles.lazadaHeader}>
                                    <div className={Styles.lazadaIcon}>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                                            <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </div>
                                    <h3>Lazada Product Details</h3>
                                </div>

                                <div className={Styles.infoGrid}>
                                    <div className={Styles.infoItem}>
                                        <div className={Styles.infoContent}>
                                            <label>Brand</label>
                                            <p>{productDetails.lazada_product.data?.attributes?.brand || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className={Styles.infoItem}>
                                        <div className={Styles.infoContent}>
                                            <label>Product Name</label>
                                            <p>{productDetails.lazada_product.data?.attributes?.name || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className={Styles.infoItem}>
                                        <div className={Styles.infoContent}>
                                            <label>Status</label>
                                            <span className={`${Styles.statusBadge} ${Styles[productDetails.lazada_product.data?.status?.toLowerCase()]}`}>
                                                {productDetails.lazada_product.data?.status || 'N/A'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className={Styles.infoItem}>
                                        <div className={Styles.infoContent}>
                                            <label>Category</label>
                                            <p>{categoryName}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* SKU Variants */}
                                {lazadaSkus.length > 0 && (
                                    <div className={Styles.skuSection}>
                                        <h4>SKU Variants</h4>

                                        {lazadaSkus.length > 1 && (
                                            <div className={Styles.skuTabs}>
                                                {lazadaSkus.map((sku, index) => (
                                                    <button
                                                        key={index}
                                                        className={`${Styles.skuTab} ${activeSkuIndex === index ? Styles.activeTab : ''}`}
                                                        onClick={() => setActiveSkuIndex(index)}
                                                    >
                                                        {sku.SellerSku || `SKU ${index + 1}`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        <div className={Styles.skuDetails}>
                                            <div className={Styles.infoGrid}>
                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>SKU ID</label>
                                                        <p>{currentSku.SkuId || 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Seller SKU</label>
                                                        <p>{currentSku.SellerSku || 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Shop SKU</label>
                                                        <p>{currentSku.ShopSku || 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Status</label>
                                                        <span className={`${Styles.statusBadge} ${Styles[currentSku.Status?.toLowerCase()]}`}>
                                                            {currentSku.Status || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Price</label>
                                                        <p className={Styles.price}>{currentSku.price ? `$${currentSku.price}` : 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Special Price</label>
                                                        <p className={Styles.specialPrice}>{currentSku.special_price ? `$${currentSku.special_price}` : 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Quantity</label>
                                                        <p>{currentSku.quantity || 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Available</label>
                                                        <p>{currentSku.Available || 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Dimensions</label>
                                                        <p>
                                                            {currentSku.package_length || '0'} × {currentSku.package_width || '0'} × {currentSku.package_height || '0'} cm
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className={Styles.infoItem}>
                                                    <div className={Styles.infoContent}>
                                                        <label>Weight</label>
                                                        <p>{currentSku.package_weight || 'N/A'} g</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {currentSku.Url && (
                                                <a
                                                    href={currentSku.Url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={Styles.lazadaLink}
                                                >
                                                    View on Lazada
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333M10 2H14M14 2V6M14 2L6.66667 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </a>
                                            )}

                                            {/* Warehouse Inventory */}
                                            {currentSku.multiWarehouseInventories?.length > 0 && (
                                                <div className={Styles.warehouseSection}>
                                                    <h5>Warehouse Inventory</h5>
                                                    <div className={Styles.warehouseGrid}>
                                                        {currentSku.multiWarehouseInventories.map((warehouse, index) => (
                                                            <div key={index} className={Styles.warehouseCard}>
                                                                <h6>{warehouse.warehouseCode}</h6>
                                                                <div className={Styles.warehouseInfo}>
                                                                    <span>Type: <strong>{warehouse.warehouseType || 'N/A'}</strong></span>
                                                                    <span>Sellable: <strong className={Styles.sellable}>{warehouse.sellableQuantity || '0'}</strong></span>
                                                                    <span>Total: <strong>{warehouse.totalQuantity || '0'}</strong></span>
                                                                    <span>Occupied: <strong>{warehouse.occupyQuantity || '0'}</strong></span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductView;